import { BASE_BACKEND_URL, fetchWithAuth } from '../../../auth'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'


const SEARCH_DEBOUNCE_MS = 300
const DEFAULT_PER_PAGE = 25

const normalizeOption = (type, option) => {
  if (!option) return null

  if (type === 'clients') {
    return {
      id: option?.id || option?.db_ref,
      db_ref: option?.db_ref || '',
      name: option?.name || '',
      label: option?.label || [option?.db_ref, option?.name].filter(Boolean).join(' - '),
      value: option?.value || option?.db_ref || option?.id,
      raw: option
    }
  }

  return {
    id: option?.id || option?.value || option?.label,
    name: option?.name || option?.label || '',
    label: option?.label || option?.name || '',
    value: option?.value || option?.id || option?.label,
    raw: option
  }
}

const mergeOptions = (currentOptions = [], incomingOptions = []) => {
  const nextOptions = [...currentOptions]

  incomingOptions.forEach((option) => {
    const alreadyExists = nextOptions.some((currentOption) => currentOption.id === option.id)

    if (!alreadyExists) {
      nextOptions.push(option)
    }
  })

  return nextOptions
}

const useStoreRelationBaseInfoOptions = ({
  type,
  enabled = true,
  searchTerm = '',
  supplierId = null,
  buyerId = null,
  storeReference = ''
}) => {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const abortControllerRef = useRef(null)
  const debounceTimeoutRef = useRef(null)
  const latestRequestKeyRef = useRef('')
  const loadedPagesRef = useRef(new Set())
  const inFlightPagesRef = useRef(new Set())
  const optionsCacheRef = useRef(new Map())

  const normalizedSearchTerm = searchTerm.trim()
  const requestKey = useMemo(() => JSON.stringify({
    type,
    enabled,
    search: normalizedSearchTerm,
    supplierId,
    buyerId,
    storeReference
  }), [type, enabled, normalizedSearchTerm, supplierId, buyerId, storeReference])

  const fetchOptions = useCallback(async ({ page = 1, append = false, signal } = {}) => {
    if (!enabled || !type) {
      setOptions([])
      setHasMore(false)
      setLoading(false)
      loadedPagesRef.current = new Set()
      inFlightPagesRef.current = new Set()
      return
    }

    const cacheKey = `${requestKey}::page=${page}`

    if (optionsCacheRef.current.has(cacheKey)) {
      const cachedResponse = optionsCacheRef.current.get(cacheKey)
      const normalizedOptions = Array.isArray(cachedResponse?.options)
        ? cachedResponse.options.map((option) => normalizeOption(type, option)).filter(Boolean)
        : []

      loadedPagesRef.current.add(page)
      setOptions((prev) => (append ? mergeOptions(prev, normalizedOptions) : normalizedOptions))
      setHasMore(Boolean(cachedResponse?.pagination?.has_more))
      return
    }

    if (inFlightPagesRef.current.has(page)) {
      return
    }

    const query = new URLSearchParams({
      type,
      page: String(page),
      per_page: String(DEFAULT_PER_PAGE)
    })

    if (normalizedSearchTerm) {
      query.set('search', normalizedSearchTerm)
    }

    if (supplierId) {
      query.set('supplier_id', String(supplierId))
    }

    if (buyerId) {
      query.set('buyer_id', String(buyerId))
    }

    if (storeReference) {
      query.set('store_reference', storeReference)
    }

    inFlightPagesRef.current.add(page)

    try {
      const response = await fetchWithAuth(
        `${BASE_BACKEND_URL}/poc/store_relations_overview/base_info_options?${query.toString()}`,
        { signal }
      )

      optionsCacheRef.current.set(cacheKey, response)
      loadedPagesRef.current.add(page)

      const normalizedOptions = Array.isArray(response?.options)
        ? response.options.map((option) => normalizeOption(type, option)).filter(Boolean)
        : []

      setOptions((prev) => (append ? mergeOptions(prev, normalizedOptions) : normalizedOptions))
      setHasMore(Boolean(response?.pagination?.has_more))
    } finally {
      inFlightPagesRef.current.delete(page)
    }
  }, [buyerId, enabled, normalizedSearchTerm, requestKey, storeReference, supplierId, type])

  useEffect(() => {
    clearTimeout(debounceTimeoutRef.current)
    abortControllerRef.current?.abort()
    loadedPagesRef.current = new Set()
    inFlightPagesRef.current = new Set()

    if (!enabled || !type) {
      setOptions([])
      setHasMore(false)
      setLoading(false)
      return undefined
    }

    latestRequestKeyRef.current = requestKey

    debounceTimeoutRef.current = setTimeout(async () => {
      const controller = new AbortController()
      abortControllerRef.current = controller

      try {
        setLoading(true)
        await fetchOptions({ page: 1, append: false, signal: controller.signal })
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('useStoreRelationBaseInfoOptions error', error)
          setOptions([])
          setHasMore(false)
        }
      } finally {
        if (!controller.signal.aborted && latestRequestKeyRef.current === requestKey) {
          setLoading(false)
        }
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      clearTimeout(debounceTimeoutRef.current)
      abortControllerRef.current?.abort()
    }
  }, [fetchOptions, requestKey, type, enabled])

  const loadMore = async () => {
    if (!enabled || loading || !hasMore) return

    const nextPage = Math.floor(options.length / DEFAULT_PER_PAGE) + 1
    const alreadyLoaded = loadedPagesRef.current.has(nextPage)
    const alreadyLoading = inFlightPagesRef.current.has(nextPage)

    if (alreadyLoaded || alreadyLoading) return

    try {
      setLoading(true)
      await fetchOptions({ page: nextPage, append: true })
    } catch (error) {
      console.error('useStoreRelationBaseInfoOptions loadMore error', error)
    } finally {
      setLoading(false)
    }
  }

  return {
    options,
    loading,
    hasMore,
    loadMore
  }
}

export default useStoreRelationBaseInfoOptions
